'use strict';

const { find, forEach, get, has, includes, keyBy, replace } = require('lodash');

const competitorKpiFields = ['competitorIndex', 'liveCompetitorDistanceScenario', 'competitorKey'];

/**
 * Merges updated KPI fields from a kpiResult competitor into a product competitor in place.
 *
 * @param {Object} productCompetitor - The competitor object on the product to update.
 * @param {Object|undefined} kpiCompetitor - The matching competitor from the kpiResult response.
 */
const mergeCompetitorKpiFields = (productCompetitor, kpiCompetitor) => {
  forEach(competitorKpiFields, field => {
    if (kpiCompetitor && has(kpiCompetitor, field)) {
      productCompetitor[field] = get(kpiCompetitor, field);
    }
  });
};

// Normalize scenario price from either object or numeric shapes
const normalizeScenarioPrice = (scenarioPriceVal, kpiResult) => {
  if (scenarioPriceVal && typeof scenarioPriceVal === 'object') {
    return {
      price: scenarioPriceVal.price,
      pricePerNormWeight: scenarioPriceVal.pricePerNormWeight || scenarioPriceVal.price,
      pricePerContentUnit: scenarioPriceVal.pricePerContentUnit,
    };
  }

  return {
    price: scenarioPriceVal,
    pricePerNormWeight: get(kpiResult, 'scenarioPricePerNormWeight', scenarioPriceVal),
    pricePerContentUnit: get(kpiResult, 'scenarioPricePerContentUnit'),
  };
};

/**
 * Updates a product object with KPI results, competitor information, and alerts.
 *
 * This function is extracted from webtool/client/src/js/store/modules/grid-view.js to be used in both FE and BE.
 *
 * Used in FE directly after a price override response is returned.
 * Used in BE in grid view response to update results with unsaved kpiResults (to avoid the issue when collapsing/expanding PG loses unsaved kpiResults).
 *
 * @param {Object} params.product - The product object to be updated
 * @param {Object} params.kpiResult - The KPI result data containing updated values
 * @param {Object} params.alerts - Alerts object indexed by product keys
 * @param {boolean} params.readFromDatabricks - Feature flag indicating whether to read from Databricks
 * @param {boolean} params.isAgGridProductListEnabled - Feature flag indicating whether AgGrid product list is enabled
 * @param {Array} params.ignoreFields - list of fields to ignore during the update
 * @returns {void} - Modifies the product object in place
 *
 */
const updateProductWithUnsavedKpiResults = ({
  product,
  kpiResult,
  alerts,
  readFromDatabricks,
  isAgGridProductListEnabled,
  ignoreFields = [],
}) => {
  // Update scalar product fields from kpiResult. 'competitors' is deliberately excluded here
  // and handled separately below: replacing product.competitors wholesale would discard price
  // and key data for competitors absent from the override response (e.g. a newly-selected
  // competitor whose index was not yet recalculated).
  const fieldsToIgnore = [...ignoreFields, 'competitors'];
  forEach(Object.keys(kpiResult), key => {
    if (has(product, key) && !includes(fieldsToIgnore, key) && product[key] !== kpiResult[key]) {
      if (key === 'scenarioPrice') {
        const normalized = normalizeScenarioPrice(kpiResult[key], kpiResult);
        product[key] = normalized.price;
        // show regular price if normWeight price is null
        product.scenarioPricePerNormWeight = normalized.pricePerNormWeight;
        product.scenarioPricePerContentUnit = normalized.pricePerContentUnit;
      } else if (includes(key, 'PerNormWeight')) {
        product[key] = kpiResult[key] || kpiResult[replace(key, 'PerNormWeight', '')];
      } else product[key] = kpiResult[key];
    }
  });

  // Merge KPI fields back into the existing product.competitors.
  // lodash forEach handles both object (keyed by competitorKey) and array forms so that
  // all original competitors are preserved regardless of what the override response contained.
  if (product.competitors && kpiResult.competitors) {
    forEach(product.competitors, comp => {
      const kpiCompetitor = find(kpiResult.competitors, {
        competitorName: comp.competitorName,
      });
      mergeCompetitorKpiFields(comp, kpiCompetitor);
    });
  }

  if (!readFromDatabricks) {
    product.competitor1.competitorIndex = get(
      find(kpiResult.competitors, {
        competitorName: product.competitor1.competitorName,
      }),
      'competitorIndex'
    );
    product.competitor2.competitorIndex = get(
      find(kpiResult.competitors, {
        competitorName: product.competitor2.competitorName,
      }),
      'competitorIndex'
    );
  }

  // If product.competitors was left as an array by a prior code path, convert it to the
  // keyed object that ag-grid field paths (competitors.<key>.competitorPrice) expect.
  if (isAgGridProductListEnabled && Array.isArray(product.competitors)) {
    product.competitors = keyBy(product.competitors, 'competitorKey');
  }

  // Update the alerts for the product after an override
  product.alerts = get(alerts, product.productKey, []);
};

module.exports = {
  updateProductWithUnsavedKpiResults,
};
